import { useState } from 'react';

import { cn, ia, iaRa, io, pluralize } from 'lib/helpers/utility';

import Badge from 'components/shared/Badge/Badge';
import Button from 'components/shared/Buttons/Button/Button';
import DateRangeSelector from 'components/shared/DateRangeSelector/DateRangeSelector';

import { getActiveFilters, getFormulaOptions, textOptions } from '../lib/helpers';

import InfiniteDropdown from './InfiniteDropdown';
import RangeSelector from './RangeSelector';
import TextInput from './TextInput';

const rangeTypes = ['range', 'number', 'number-range'];

const AppliedFilters = ({ filters = {}, defaultFilters = {}, setFilters, visibleCount }) => {
  const [count, setCount] = useState();

  const appliedFilters = getActiveFilters(filters);

  if (!ia(appliedFilters)) return null;

  const visibleFilters = count ? appliedFilters.slice(0, count) : appliedFilters;

  const handleShowMore = () => {
    if (!visibleCount) return;
    setCount((prev) => (prev === undefined ? visibleCount : undefined));
  };

  return (
    <>
      {visibleFilters.map(([key, filter]) => {
        return iaRa(filter).map((_filter, index) => {
          const { title, icon, values, formula, type } = _filter;
          const pluralLabel = ia(values) && pluralize(title || '', values.length, ['uploaded by']);

          const [item] = (ia(values) && values) || [];

          const handleFormulaChange = (formula) => {
            setFilters((prev) => ({
              ...prev,
              [key]: prev[key].map((f, filterIndex) =>
                filterIndex === index ? { ...f, formula: formula.value } : f
              )
            }));
          };

          const handleOutsideClick = (items) => {
            if (!ia(items) && !io(items) && typeof items !== 'number') {
              setFilters((prev) => {
                const { [key]: _, ...rest } = prev;
                return rest;
              });
              return;
            }

            setFilters((prev) => ({
              ...prev,
              [key]: prev[key].map((f, filterIndex) => {
                return filterIndex === index
                  ? {
                      ...f,
                      values: items,
                      formula: formula || filters[key]?.formula || 'is',
                      ...(ia(items) &&
                        (formula || filters[key].formula) !== 'is not' && {
                          formula: items.length > 1 ? 'is any of' : 'is'
                        })
                    }
                  : f;
              })
            }));
          };

          const buttonText = !ia(values, 1)
            ? item?.label
            : `${values.length} ${pluralLabel.toLowerCase()}`;

          return (
            <Button.Group key={key} variant="tertiary" size="xs">
              <Button
                icon={icon}
                text={title}
                iconColor="neutral"
                className="pointer-events-none whitespace-nowrap hover:bg-transparent active:bg-transparent"
              />
              <Button.Dropdown
                items={getFormulaOptions(type, iaRa(values)?.length, values)}
                dropdownClassName="rounded-[4px]"
                onClick={handleFormulaChange}
                active={formula}
                text={formula}
                className="whitespace-nowrap border-x-0"
                index={index}
              />
              {type === 'date-range' ? (
                <DateRangeSelector
                  value={values}
                  onChange={handleOutsideClick}
                  buttonClassName={cn(
                    'whitespace-nowrap rounded-none',
                    _filter.default && 'rounded-r-md'
                  )}
                  index={index}
                />
              ) : rangeTypes.includes(type) ? (
                <RangeSelector
                  values={values}
                  onApply={handleOutsideClick}
                  buttonClassName={cn(
                    'whitespace-nowrap rounded-none',
                    _filter.default && 'rounded-r-md'
                  )}
                  initialOpenState={!values}
                  formula={formula}
                  index={index}
                />
              ) : type === 'text' && textOptions.includes(formula) ? (
                <TextInput
                  values={values}
                  onApply={handleOutsideClick}
                  formula={formula}
                  initialOpenState={!values}
                  index={index}
                />
              ) : (
                <InfiniteDropdown
                  items={values}
                  data={defaultFilters[key]}
                  dropdownClassName="absolute m-0 z-50 shadow-xs"
                  onChange={handleOutsideClick}
                  active={iaRa(values).includes(item?.value)}
                  text={buttonText}
                  withButton
                  index={index}
                />
              )}
              {!_filter.default && (
                <Button
                  icon="close-linear"
                  iconColor="neutral"
                  className="border-l-0"
                  iconShade={700}
                  iconSize={16}
                  onClick={() => {
                    setFilters((prev) => {
                      const filteredArray = prev[key].filter(
                        (f, filterIndex) => filterIndex !== index
                      );
                      if (!ia(filteredArray)) {
                        const { [key]: _, ...rest } = prev;
                        return rest;
                      }
                      return {
                        ...prev,
                        [key]: filteredArray
                      };
                    });
                  }}
                />
              )}
            </Button.Group>
          );
        });
      })}
      {visibleCount && visibleCount < appliedFilters.length && (
        <Badge
          color="neutral"
          size="medium"
          text={
            count < appliedFilters.length ? `+${appliedFilters.length - count} more` : 'Show less'
          }
          onClick={handleShowMore}
          isRounded={false}
        />
      )}
    </>
  );
};

export default AppliedFilters;
